import { useEffect, useRef, useState } from 'react';
import type { Sample } from '../data/types';
import { detectFaceCount } from '../ml/faceBias';

/**
 * Runs face detection in the background for image samples we haven't
 * scored yet, and returns a stable Map<sampleId, faceCount> that
 * `observeFaceBias` can read from.
 *
 * Detection is best-effort: if the model fails to load (kid is offline,
 * WebGL unavailable on a $200 Chromebook with a quirky GPU driver) we
 * silently skip the bias hint rather than blocking the UI. The kid is
 * making something, not failing a test.
 */
export function useFaceBias(
  samples: Pick<Sample, 'id' | 'blob'>[],
  enabled: boolean,
): ReadonlyMap<string, number> {
  const [counts, setCounts] = useState<ReadonlyMap<string, number>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Capture the Set reference at effect start so the cleanup uses the
    // same instance React-Hooks lint expects.
    const inFlightSet = inFlight.current;
    const pending = samples.filter((s) => !counts.has(s.id) && !inFlightSet.has(s.id));
    if (pending.length === 0) return;

    pending.forEach((s) => inFlightSet.add(s.id));
    let aborted = false;

    void (async () => {
      for (const sample of pending) {
        if (aborted || cancelled.current) break;
        try {
          const n = await detectFaceCount(sample.blob);
          if (aborted || cancelled.current) break;
          setCounts((prev) => {
            if (prev.has(sample.id)) return prev;
            const next = new Map(prev);
            next.set(sample.id, n);
            return next;
          });
        } catch {
          // Best-effort: drop this sample's score and move on.
        } finally {
          inFlightSet.delete(sample.id);
        }
      }
    })();

    return () => {
      aborted = true;
      pending.forEach((s) => inFlightSet.delete(s.id));
    };
  }, [samples, enabled, counts]);

  return counts;
}
